<template>
  <ModalComponent
    :active="active"
    :titleModal="'Comité de ofertas'"
    :optionalText="'Si el usuario pertenece al comité, podrá votar en las ofertas dentro del portal de solicitudes.'"
    @hdlOnClick="saveCommitteeSettings"
    @cancel="closeCommitteeModal"
    :loader="loadingCommittee"
    :disabled="loadingCommittee || loadingCommitteePermission || !committeeStatusLoaded || !canEditCommitteeSettings"
    textSave="Guardar"
  >
    <div class="has-text-left committee-modal">
      <template v-if="loadingCommitteeDetail">
        <div class="field">
          <div class="committee-modal__row committee-modal__row--padded committee-modal__row--first">
            <span class="committee-modal__skeleton committee-modal__skeleton--label"></span>
            <span class="committee-modal__skeleton committee-modal__skeleton--value"></span>
          </div>
        </div>
        <div class="field committee-modal__row committee-modal__row--highlight">
          <span class="committee-modal__skeleton committee-modal__skeleton--label"></span>
          <span class="committee-modal__skeleton committee-modal__skeleton--pill"></span>
        </div>
        <div class="field committee-modal__row committee-modal__row--padded committee-modal__row--spaced">
          <div>
            <span class="committee-modal__skeleton committee-modal__skeleton--label"></span>
            <span class="committee-modal__skeleton committee-modal__skeleton--text"></span>
          </div>
          <span class="committee-modal__skeleton committee-modal__skeleton--switch"></span>
        </div>
        <div class="field committee-modal__row committee-modal__row--highlight committee-modal__row--spaced">
          <span class="committee-modal__skeleton committee-modal__skeleton--label"></span>
          <span class="committee-modal__skeleton committee-modal__skeleton--input"></span>
        </div>
      </template>
      <template v-else>
        <div class="field">
          <div class="committee-modal__row committee-modal__row--padded committee-modal__row--first">
            <label class="label committee-modal__muted">Correo electrónico</label>
            <p class="committee-modal__email">{{ user ? user.email : '-' }}</p>
          </div>
        </div>
        <div class="field committee-modal__row committee-modal__row--highlight">
          <label class="label committee-modal__muted mb-0">Estatus</label>
          <span
            class="is-status"
            :class="committeePermission.permission ? 'is-status-active' : 'is-status-inactive'"
          >
            {{ committeePermission.permission ? 'Pertenece al comité' : 'No pertenece al comité' }}
          </span>
        </div>
        <div class="field committee-modal__row committee-modal__row--padded committee-modal__row--spaced">
          <div>
            <label class="label committee-modal__muted mb-0">Habilitado en comité</label>
            <p class="committee-modal__toggle-help">Permite que el usuario participe en votaciones</p>
          </div>
          <label class="committee-switch" :class="{ 'committee-switch--disabled': loadingCommittee || !committeeStatusLoaded || isSwitchLockedByFirstAssignment }">
            <input
              v-model="committeeFormEnabled"
              type="checkbox"
              :disabled="loadingCommittee || loadingCommitteePermission || !committeeStatusLoaded || isSwitchLockedByFirstAssignment || !canEditCommitteeSettings"
            >
            <span class="committee-switch__slider"></span>
          </label>
        </div>
        <div v-if="isSwitchLockedByFirstAssignment" class="committee-modal__info-alert" role="note" aria-live="polite">
          <p class="committee-modal__info-alert-text">
            Al agregarlo por primera vez al comité quedará habilitado para votar y el valor de puntos debe ser mayor a 0.
          </p>
        </div>
        <div class="field committee-modal__row committee-modal__row--highlight committee-modal__row--spaced">
          <label class="label committee-modal__muted mb-0">Valor de puntos</label>
          <input
            v-model="committeeFormPoints"
            class="input committee-modal__points-input"
            type="text"
            inputmode="numeric"
            :disabled="isPointsInputDisabled || !canEditCommitteeSettings"
            placeholder="0"
            @keypress="handlePointsKeypress"
          >
        </div>
      </template>
      <div v-if="committeeStatusLoaded && !canEditCommitteeSettings" class="committee-modal__danger-alert" role="alert" aria-live="polite">
        <p class="committee-modal__danger-alert-text">
          No cuentas con permisos para editar la configuración del comité.
        </p>
      </div>
      <p class="help is-danger">{{ committeeError }}</p>
    </div>
  </ModalComponent>
</template>

<script>
import { mapActions } from 'vuex'

const defaultCommitteePermission = () => ({
  permission: false,
  enabled: false,
  points: null
})

export default {
  name: 'ModalCommittee',
  components: {
    ModalComponent: () => import('@/components/ui/ModalComponent.vue')
  },
  props: {
    active: {
      type: Boolean,
      default: false
    },
    user: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      loadingCommittee: false,
      loadingCommitteeDetail: false,
      loadingCommitteePermission: false,
      committeeStatusLoaded: false,
      committeePermission: defaultCommitteePermission(),
      initialCommitteePermission: false,
      committeeFormEnabled: false,
      committeeFormPoints: 0,
      canEditCommitteeSettings: false,
      committeeError: ''
    }
  },
  computed: {
    isSwitchLockedByFirstAssignment () {
      return this.committeeStatusLoaded && !this.initialCommitteePermission
    },
    isPointsInputDisabled () {
      return this.loadingCommittee || !this.committeeStatusLoaded || (this.initialCommitteePermission && !this.committeeFormEnabled)
    }
  },
  watch: {
    active (isActive) {
      if (isActive) {
        this.loadCommitteePermission()
        return
      }
      this.resetCommitteeState()
    },
    user: {
      deep: false,
      handler (nextUser, previousUser) {
        if (!this.active) return
        if (nextUser?.id === previousUser?.id) return
        this.loadCommitteePermission()
      }
    }
  },
  methods: {
    ...mapActions(['getOfferVoterPermission', 'setOfferVoterPermission', 'getServicePermission']),
    async loadCommitteePermission () {
      this.committeeError = ''
      this.committeePermission = defaultCommitteePermission()
      this.initialCommitteePermission = false
      this.committeeFormEnabled = false
      this.committeeFormPoints = 0
      this.committeeStatusLoaded = false
      this.canEditCommitteeSettings = false

      if (!this.user?.id) {
        this.committeeError = 'No fue posible consultar el comité'
        return
      }

      this.loadingCommittee = true
      this.loadingCommitteeDetail = true
      this.loadingCommitteePermission = true
      try {
        const [response, permissionsResponse] = await Promise.all([
          this.getOfferVoterPermission(this.user.id),
          this.getServicePermission(['setOfferVoterPermission'])
        ])

        const enabledCodes = (permissionsResponse || [])
          .filter(service => service?.enabled)
          .map(service => service?.code)
        this.canEditCommitteeSettings = enabledCodes.includes('setOfferVoterPermission')

        if (!response) {
          this.committeeError = 'No fue posible consultar el comité'
          return
        }

        this.committeePermission = {
          permission: !!response.permission,
          enabled: !!response.enabled,
          points: Number(response.points) || 0
        }
        this.initialCommitteePermission = !!response.permission
        this.committeeFormEnabled = !!response.enabled
        this.committeeFormPoints = Number(response.points) || 0
        this.committeeStatusLoaded = true
      } catch (_) {
        this.committeeError = 'No fue posible consultar el comité'
      } finally {
        this.loadingCommittee = false
        this.loadingCommitteeDetail = false
        this.loadingCommitteePermission = false
      }
    },
    resetCommitteeState () {
      this.loadingCommittee = false
      this.loadingCommitteeDetail = false
      this.loadingCommitteePermission = false
      this.committeeStatusLoaded = false
      this.committeePermission = defaultCommitteePermission()
      this.initialCommitteePermission = false
      this.committeeFormEnabled = false
      this.committeeFormPoints = 0
      this.canEditCommitteeSettings = false
      this.committeeError = ''
    },
    async saveCommitteeSettings () {
      if (!this.committeeStatusLoaded || !this.canEditCommitteeSettings) return
      this.committeeError = ''

      const normalizedPoints = Number(this.committeeFormPoints)
      const isFirstAssignment = !this.initialCommitteePermission
      if (isFirstAssignment && normalizedPoints <= 0) {
        this.committeeError = 'El valor de puntos debe ser mayor a 0 para el primer registro en comité'
        return
      }
      const enabledToSend = isFirstAssignment ? true : !!this.committeeFormEnabled
      const pointsToSend = (this.initialCommitteePermission && !this.committeeFormEnabled)
        ? 0
        : (Number.isInteger(normalizedPoints) ? normalizedPoints : 0)

      this.loadingCommittee = true
      let response
      try {
        response = await this.setOfferVoterPermission({
          userId: this.user?.id,
          enabled: enabledToSend,
          points: pointsToSend
        })
      } catch (_) {
        this.committeeError = 'No fue posible actualizar el comité'
      } finally {
        this.loadingCommittee = false
      }

      if (!response) {
        if (!this.committeeError) {
          this.committeeError = 'No fue posible actualizar el comité'
        }
        return
      }

      this.committeePermission = {
        permission: !!response.permission,
        enabled: !!response.enabled,
        points: Number(response.points) || 0
      }
      this.initialCommitteePermission = !!response.permission
      this.committeeFormEnabled = !!response.enabled
      this.committeeFormPoints = Number(response.points) || 0

      this.$emit('saved', {
        userId: this.user?.id,
        ...this.committeePermission
      })

      this.closeCommitteeModal()
    },
    closeCommitteeModal () {
      this.$emit('cancel')
    },
    handlePointsKeypress (event) {
      if (event.ctrlKey || event.metaKey || event.altKey) return
      if (event.key.length > 1) return
      if (!/^\d$/.test(event.key)) {
        event.preventDefault()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.committee-modal {
  padding: 4px 10px 6px;
  margin: 0 auto;

  &__row {
    display: grid;
    grid-template-columns: 1fr auto;
    align-items: center;
    gap: 22px;
  }

  &__row--padded {
    padding: 0 22px;
  }

  &__row--highlight {
    margin: 16px 0 24px;
    background: #f3f3f4;
    border-radius: 4px;
    padding: 18px 22px;
  }

  &__row--spaced {
    margin: 12px 0 24px;
  }

  &__row--first {
    margin-bottom: 16px;
  }

  &__muted {
    color: #8ea0b3;
    margin-bottom: 0;
    font-weight: 600;
  }

  &__email {
    font-size: 13px;
    font-weight: 500;
    color: var(--intelligential-color);
    margin: 0;
    word-break: break-word;
  }

  .is-status {
    justify-self: end;
    min-width: 220px;
    font-size: 12px;
  }

  &__toggle-help {
    margin: 0;
    font-size: 13px;
    line-height: 1.3;
    color: var(--color-label-intelligential);
  }

  &__points-input {
    height: 35px;
    border: 1px solid #d3d3d3;
    border-radius: 8px;
    width: 220px;
    font-size: 14px;
    color: #8d8d8d;
    padding: 0 14px;
    box-shadow: none;
    justify-self: end;
  }

  &__points-input:disabled {
    background-color: #f5f5f5;
    color: #8d8d8d;
    cursor: not-allowed;
  }

  .help.is-danger {
    font-size: 13px;
    margin-top: 8px;
  }

  &__info-alert {
    margin: -12px 22px 12px;
    padding: 10px 14px;
    border-radius: 4px;
    background: rgba(76, 112, 255, 0.2);
    text-align: center;
  }

  &__info-alert-title {
    margin: 0 0 4px;
    font-size: 12px;
    font-weight: 600;
    color: #4c70ff;
    line-height: 1.2;
  }

  &__info-alert-text {
    margin: 0;
    font-size: 12px;
    font-weight: 500;
    color: #4c70ff;
    line-height: 1.3;
  }

  &__skeleton {
    display: inline-block;
    border-radius: 6px;
    background: linear-gradient(90deg, #ececec 25%, #f5f5f5 37%, #ececec 63%);
    background-size: 400% 100%;
    animation: committee-skeleton-loading 1.2s ease-in-out infinite;
  }

  &__skeleton--label {
    width: 130px;
    height: 14px;
  }

  &__skeleton--value {
    width: 220px;
    height: 14px;
    justify-self: end;
  }

  &__skeleton--pill {
    width: 220px;
    height: 24px;
    border-radius: 999px;
    justify-self: end;
  }

  &__skeleton--text {
    display: block;
    width: 200px;
    height: 12px;
    margin-top: 8px;
  }

  &__skeleton--switch {
    width: 53px;
    height: 27px;
    border-radius: 999px;
    justify-self: end;
  }

  &__skeleton--input {
    width: 220px;
    height: 35px;
    justify-self: end;
  }

  &__danger-alert {
    margin: -6px 22px 10px;
    padding: 10px 14px;
    border-radius: 4px;
    background: rgba(255, 56, 96, 0.16);
    text-align: center;
  }

  &__danger-alert-text {
    margin: 0;
    font-size: 12px;
    font-weight: 500;
    color: #ff3860;
    line-height: 1.3;
  }
}

@keyframes committee-skeleton-loading {
  0% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0 50%;
  }
}

.committee-switch {
  position: relative;
  display: inline-block;
  width: 53px;
  height: 27px;
}

.committee-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.committee-switch__slider {
  position: absolute;
  cursor: pointer;
  inset: 0;
  background-color: #d6d6d8;
  transition: 0.2s;
  border-radius: 999px;
}

.committee-switch__slider::before {
  content: '';
  position: absolute;
  height: 17px;
  width: 17px;
  left: 5px;
  top: 5px;
  background-color: #f7f7f7;
  transition: 0.2s;
  border-radius: 50%;
}

.committee-switch input:checked + .committee-switch__slider {
  background-color: #59c891;
}

.committee-switch input:checked + .committee-switch__slider::before {
  transform: translateX(23px);
}

.committee-switch--disabled {
  opacity: 0.7;
}
</style>
